// A partner's Billing page: their Stripe connection and their clients' concierge
// payment requests (spec 2026-11-03-concierge-stripe-payments §1, §6).
//
// Owners and team members both open it (members are role 'partner' on the wire),
// but only the owner can link Stripe: the API refuses /connect/start for a member,
// so a member who is not connected is told to ask the owner instead.
//
// The Connect OAuth callback lands back here with ?connected=1 or ?connect_error=1;
// the banner is read once and the query is then dropped, so a refresh or a
// bookmark does not show it again.
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import Button from '../components/Button';
import ConciergeBillingTable from '../components/billing/ConciergeBillingTable';
import { authConfig, errorDetail } from '../lib/conciergeBilling';

function StripeStatus({ status, isOwner, connecting, onConnect }) {
  if (status.connected) {
    // Reconnecting runs the same OAuth flow: it picks up a finished setup, or
    // links a different account.
    return (
      <>
        <p><strong>{status.business_name || 'Your Stripe account'}</strong></p>
        <p>{status.charges_enabled ? 'Payments enabled' : 'Finish setting up your Stripe account'}</p>
        {isOwner && (
          <div>
            <Button variant={status.charges_enabled ? 'secondary' : 'accent'} onClick={onConnect}
                    loading={connecting} loadingText="Opening Stripe…">
              Reconnect Stripe
            </Button>
          </div>
        )}
      </>
    );
  }
  if (!isOwner) return <p>Ask your account owner to connect Stripe</p>;
  return (
    <>
      <p>Connect your Stripe account to take concierge payments from your clients.</p>
      <div>
        <Button variant="accent" onClick={onConnect} loading={connecting} loadingText="Opening Stripe…">
          Connect with Stripe
        </Button>
      </div>
    </>
  );
}

export default function PartnerBillingPage() {
  const { user, API_URL } = useAuth();
  const [params, setParams] = useSearchParams();
  const isOwner = user?.role === 'partner' && !user?.is_partner_member;
  // Read once on arrival from the OAuth callback; the effect below drops the query.
  const [justConnected] = useState(() => params.get('connected') === '1');
  const [callbackFailed] = useState(() => params.get('connect_error') === '1');

  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState(false);
  const [rows, setRows] = useState(null);
  const [rowsErr, setRowsErr] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectErr, setConnectErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/concierge-billing/connect/status`, authConfig())
      .then((r) => { if (!cancelled) setStatus(r.data || {}); })
      .catch(() => { if (!cancelled) setStatusErr(true); });
    axios.get(`${API_URL}/concierge-billing/requests`, authConfig())
      .then((r) => { if (!cancelled) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setRowsErr(true); });
    return () => { cancelled = true; };
  }, [API_URL]);

  // Once, on arrival (setParams is not stable across renders, hence the ref).
  const queryCleared = useRef(false);
  useEffect(() => {
    if (queryCleared.current) return;
    queryCleared.current = true;
    if (justConnected || callbackFailed) setParams({}, { replace: true });
  }, [justConnected, callbackFailed, setParams]);

  const connect = async () => {
    setConnecting(true);
    setConnectErr('');
    try {
      // withCredentials: /connect/start sets the HttpOnly nonce cookie the callback
      // checks, and the browser only keeps it from a cross-origin API when asked to.
      const r = await axios.get(`${API_URL}/concierge-billing/connect/start`,
        { ...authConfig(), withCredentials: true });
      window.location.assign(r.data.url);
    } catch (err) {
      setConnectErr(errorDetail(err, 'Could not start the Stripe connection. Try again.'));
      setConnecting(false);
    }
  };

  return (
    <AdminPage active="billing" title="Billing" subtitle="Your Stripe account and your clients' concierge payments.">
      {justConnected && (
        <div role="status" className="settings-toast">Stripe is connected.</div>
      )}
      {callbackFailed && (
        <div role="alert" className="settings-toast settings-toast--error">
          We could not connect your Stripe account. Please try again.
        </div>
      )}
      {connectErr && <div role="alert" className="settings-toast settings-toast--error">{connectErr}</div>}

      <div className="adm-stack-cards">
        <div className="adm__card">
          <h2 className="adm-card__title">Stripe</h2>
          {statusErr ? (
            <p>Couldn&rsquo;t load your Stripe status. Refresh to try again.</p>
          ) : !status ? (
            <div className="adm-loading">Loading…</div>
          ) : (
            <StripeStatus status={status} isOwner={isOwner} connecting={connecting} onConnect={connect} />
          )}
        </div>

        <div className="adm__card">
          <h2 className="adm-card__title">Payment requests</h2>
          {rowsErr ? (
            <p>Couldn&rsquo;t load payment requests. Refresh to try again.</p>
          ) : !rows ? (
            <div className="adm-loading">Loading…</div>
          ) : (
            <ConciergeBillingTable rows={rows} />
          )}
        </div>
      </div>
    </AdminPage>
  );
}
